import { Box, Chip, Stack, TableCell, Typography } from '@mui/material'
import type { ReactNode } from 'react'

import type { EnergyBidPtuSource } from '@/features/bidding/types/bidDetail/EnergyBidSnapshot'

import SourceBadge from './SourceBadge'

export type BidDetailPtuCellProps = Readonly<{
  rowSpan: number
  label: string
  subLabel?: string
  source: EnergyBidPtuSource
  /** Pre-rendered mini-stats (OBL / OFF / ACT) supplied by the per-tab view. */
  meta: ReactNode
  /** True when this PTU is the current wall-clock PTU (shows a "NOW" chip). */
  isNow?: boolean
  dimmed?: boolean
}>

/**
 * Leftmost cell of the table, `rowspan`'d across all step rows of the PTU
 * (across all visible AGs).
 *
 * Pure presentation — knows nothing about which bid type is rendering. The
 * mini-stats `meta` slot is filled in by the per-tab view.
 */
export default function BidDetailPtuCell({
  rowSpan,
  label,
  subLabel,
  source,
  meta,
  isNow = false,
  dimmed = false,
}: BidDetailPtuCellProps) {
  return (
    <TableCell
      rowSpan={rowSpan}
      sx={{
        verticalAlign: 'top',
        borderRight: 1,
        borderRightColor: 'divider',
        minWidth: 100,
        opacity: dimmed ? 0.5 : 1,
      }}
    >
      <Stack spacing={0.5}>
        <Stack alignItems="center" direction="row" spacing={0.75}>
          <Typography fontWeight={600} variant="body2">
            {label}
          </Typography>
          {isNow && <Chip color="primary" label="NOW" size="small" />}
        </Stack>
        {subLabel && (
          <Typography color="text.secondary" variant="caption">
            {subLabel}
          </Typography>
        )}
        <Box>
          <SourceBadge source={source} />
        </Box>
        <Box>{meta}</Box>
      </Stack>
    </TableCell>
  )
}
